import { combineEpics } from "redux-observable";
import { from } from "rxjs";
import { filter, map, mergeMap } from "rxjs/operators";

import { actionCreators, isOfAction } from "../actions";
import { Epic } from "../utils/types";

export interface BadgeData {
    dockText?: string;
    dockBounce?: boolean;
    trayIcon?: string;
    overlayIcon?: string;
    overlayText?: string;
    flashFrame?: boolean;
}

interface DownloadData {
    id: string;
    url: string;
    filename?: string;
}

type Integration = Partial<{
    openExternal: (url: string) => void;
    focusWindow: () => Promise<boolean>;
    setBadge: (data: BadgeData) => Promise<boolean>;
    getVersion: () => Promise<string>;
    openDevTools: () => Promise<void>;
    download: (value: DownloadData) => Promise<boolean>;
    copyToClipboard: (value: string) => Promise<boolean>;
}>;

// API

export const electronIntegration: Integration | undefined = (window as any).electronIntegration;

export const isEnabled = !!electronIntegration;

export const isElectronMac =
    navigator.platform === "MacIntel" && !!navigator.userAgent.match(/Electron/);

export const wrapAPI = <
    K extends keyof Integration,
    M extends NonNullable<Integration[K]>,
    A extends Parameters<M>
>(
    method: K
) => (...args: A) => {
    if (electronIntegration && typeof electronIntegration[method] === "function") {
        return (electronIntegration[method] as ((...args: any[]) => ReturnType<M>))(...args);
    }
    return Promise.reject(new Error("nothing to run at " + method));
};

export const desktopAPI = {
    openExternal: wrapAPI("openExternal"),
    focusWindow: wrapAPI("focusWindow"),
    setBadge: wrapAPI("setBadge"),
    getVersion: wrapAPI("getVersion"),
    openDevTools: wrapAPI("openDevTools"),
    download: wrapAPI("download"),
    copyToClipboard: wrapAPI("copyToClipboard"),
};

export function isClipboardEnabled() {
    return !!(electronIntegration && typeof electronIntegration.copyToClipboard === "function");
}

// epics

const onAppIsReady: Epic = action$ =>
    action$.pipe(
        filter(isOfAction(actionCreators.appIsReady)),
        mergeMap(() => (isEnabled ? from(desktopAPI.getVersion()) : [])),
        map(version => actionCreators.setElectronVersion({ version }))
    );

export const electronIntergrationEpic = combineEpics(onAppIsReady);
